import { FormInputState, FormModel, ResultValue } from 'src_common/common/mobx-utils/Form';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { validateExpectNumber } from 'src/api/utils/formValidators';
import { TextareaStatus } from 'src/components/FormComponents/Textarea/TextareaStatus';
import { InputStatus } from 'src/components/Input/InputStatus';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { MarketFilterType } from './commonCmsGuard';

export interface StarDataModelType {
    value: number;
    name: string;
}

export class FilterStarRatingState {
    public readonly starNameModel: FormModel<string>;
    public readonly starName: FormInputState<string>;
    public readonly starNameStatus: InputStatus;

    readonly starValue: FormInputState<string>;
    readonly starValueModel: FormModel<number | null>;
    readonly starValueStatus: InputStatus;

    constructor(starItem: StarDataModelType | null) {
        this.starName = new FormInputState(starItem?.name ?? '', false);
        this.starNameModel = this.starName.toModel();
        this.starNameStatus = new InputStatus(this.starNameModel);

        this.starValue = new FormInputState(starItem !== null ? starItem.value.toString() : '', false);
        this.starValueModel = this.starValue.map(validateExpectNumber);
        this.starValueStatus = new InputStatus(this.starValueModel);
        makeAutoObservable(this);
    }

    @computed public get starData(): StarDataModelType | null {
        const starRatingResult = this.starValueModel.result;
        const starNameResult = this.starNameModel.result;

        if (starRatingResult instanceof ResultValue && starNameResult instanceof ResultValue) {
            return {
                value: starRatingResult.value ?? 0,
                name: starNameResult.value ?? '',
            };
        }

        return null;
    }

    public get starValueOptions() {
        return [
            {
                key: '',
                name: 'not set',
            },
            {
                key: '1',
                name: '1',
            },
            {
                key: '2',
                name: '2',
            },
            {
                key: '3',
                name: '3',
            },
            {
                key: '4',
                name: '4',
            },
            {
                key: '5',
                name: '5',
            },
        ];
    }
}

export class FilterDetailsState {
    @observable starRatingList: Array<number>;

    public readonly descriptionModel: FormModel<string>;
    public readonly description: FormInputState<string>;
    public readonly descriptionStatus: TextareaStatus;

    private readonly filterStarRatingMap: MobxMapAutoNew<number, FilterStarRatingState>;

    constructor(filterItemRaw: MarketFilterType | null) {
        this.starRatingList = filterItemRaw !== null ? filterItemRaw.starValues.map((_elem, index) => index) : [];

        this.description = new FormInputState(filterItemRaw?.description ?? '', false);
        this.descriptionModel = this.description.toModel();
        this.descriptionStatus = new TextareaStatus(this.descriptionModel);

        this.filterStarRatingMap = new MobxMapAutoNew(
            (_filterStarRatingId: number): FilterStarRatingState =>
                new FilterStarRatingState(
                    filterItemRaw !== null ? filterItemRaw.starValues[_filterStarRatingId] ?? null : null
                )
        );

        makeAutoObservable(this);
    }

    getStarRatingById = (id: number): FilterStarRatingState => {
        return this.filterStarRatingMap.get(id);
    };

    @action addNextStarValue = (): void => {
        const maxVal = this.starRatingList.length > 0 ? Math.max(...this.starRatingList) ?? 0 : 0;
        this.starRatingList.push(maxVal + 1);
    };

    @computed public get starFieldData(): { description: string; starData: Array<StarDataModelType> } | null {
        const descriptionResult = this.descriptionModel.result;

        if (descriptionResult instanceof ResultValue) {
            const starData: Array<StarDataModelType> = [];

            for (const starItemId of this.starRatingList) {
                const starItem = this.getStarRatingById(starItemId);
                if (starItem.starData !== null) {
                    starData.push(starItem.starData);
                }
            }

            return {
                description: descriptionResult.value ?? '',
                starData,
            };
        }

        return null;
    }

    @action setEditState = (description: string, starData: Array<StarDataModelType>): void => {
        this.description.setValue(description);
        for (const [key, star] of starData.entries()) {
            const starItem = this.getStarRatingById(key);
            starItem.starName.setValue(star.name);
            starItem.starValue.setValue(star.value.toString());
        }
    };

    @action onDeleteStarItem = (id: number): void => {
        this.starRatingList = this.starRatingList.filter((elem) => elem !== id);
        this.filterStarRatingMap.delete(id);
    };
}
